import type { PaywallPriceForm } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/types/PaywallPriceForm";
import type { Event } from "./Event";
import type { PaywallCoupon } from "./PaywallCoupon";
import type { PaywallCustomization } from "./PaywallCustomization";
import type {
  BasePaywallPrice,
  PaywallPrice,
  PaywallPriceScenario,
} from "./PaywallPrice";

export const PAYWALL_STATUS = Object.freeze({
  DRAFT: "draft",
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const);

export const PAYWALL_NON_DRAFT_STATUSES = Object.freeze([
  PAYWALL_STATUS.ACTIVE,
  PAYWALL_STATUS.INACTIVE,
] as const);

export type PaywallStatus =
  (typeof PAYWALL_STATUS)[keyof typeof PAYWALL_STATUS];

export const paywallCurrencyDirections = {
  ltr: "ltr",
  rtl: "rtl",
} as const;
export type PaywallCurrencyDirection = keyof typeof paywallCurrencyDirections;
export const paywallCurrencySmallestUnits = {
  decimal: "decimal",
  zero_decimal: "zero_decimal",
  three_decimals: "three_decimals",
} as const;
export type PaywallCurrencySmallestUnit =
  keyof typeof paywallCurrencySmallestUnits;

export type PaywallCurrencyCheckout = Pick<
  PaywallCurrency,
  "smallest_unit" | "code" | "symbol" | "direction" | "id"
>;

export interface PaywallCurrency {
  code: string;
  direction: PaywallCurrencyDirection;
  id: number;
  is_default: boolean;
  max_charge_amount: number;
  min_charge_amount: number;
  smallest_unit: PaywallCurrencySmallestUnit;
  symbol: string;
}

export const paywallTypes = {
  space: "space",
  event: "event",
} as const;
export type PaywallType = keyof typeof paywallTypes;

interface EventProps extends Event {
  amount_formatted: string;
  ended: boolean;
  event_setting: {
    starts_at: string;
  };
  recurring: boolean;
  recurring_event_setting?: {
    frequency_formatted: string;
  };
}
export interface Paywall {
  active_subscription_prices?: PaywallPrice[];
  all_subscription_prices?: PaywallPrice[];
  can_be_repurchased?: boolean;
  checkout_path?: string;
  coupons_enabled?: boolean; // TODO: Move to policies
  created_at?: string;
  currency: PaywallCurrencyCheckout;
  currency_id?: number;
  description?: string;
  destroyable?: boolean;
  display_name: string;
  event?: EventProps;
  id: number;
  internal_name?: string;
  mobile_prices_attributes: Array<PaywallPrice>;
  name?: string;
  paywall_customization: PaywallCustomization;
  paywall_group_id: number | null;
  policies?: {
    can_update_all_members_access: boolean;
    can_change_currency: boolean;
    requires_currency: boolean;
  };
  prices: Array<PaywallPrice>;
  redirect_url?: string;
  status?: PaywallStatus;
  stripe_prices_attributes: Array<PaywallPriceForm>;
  thank_you_page_enabled?: boolean;
  total_active_onetime_subscribers?: string;
  total_active_recurring_subscribers?: string;
  trial_days: number;
  trial_requires_card?: boolean;
}

export interface CouponApplication {
  applied_at: string;
  discount_amount: number;
}

export interface CouponApplications {
  application_count: number | "Infinity";
  applications: Array<CouponApplication>;
  free_forever: boolean;
  has_more: boolean;
}

export interface PaywallCheckoutPreview {
  amount_due_now: number;
  community_id: number;
  community_member_id?: number;
  community_member_subscription_id?: number;
  coupon_applications: CouponApplications;
  currency: PaywallCurrency;
  first_payment: {
    price_amount: number;
    formatted_price_amount: string;
    prorated_credit_description: string;
    prorated_credit_amount: number;
    formatted_prorated_credit_amount: string;
    subtotal: number;
    formatted_subtotal: string;
    discount: number;
    formatted_discount: string;
    tax: number;
    formatted_tax: string;
    total: number;
    formatted_total: string;
    starting_balance: number;
    formatted_starting_balance: string;
    amount_due: number;
    formatted_amount_due: string;
    carry_over_amount: number;
    formatted_carry_over_amount: string;
    ending_balance: number;
    formatted_ending_balance: string;
    due_on: string;
    formatted_due_on: string;
    due_on_phrase: string;
  };
  formatted_amount_due_now: string;
  free_forever?: boolean;
  future_payments?: {
    subtotal: number;
    formatted_subtotal: string;
    discount_phrase: string;
    discount: number;
    formatted_discount: string;
    tax: number;
    formatted_tax: string;
    total: number;
    formatted_total: string;
    starting_balance: number;
    formatted_starting_balance: string;
    amount_due: number;
    formatted_amount_due: string;
    carry_over_amount: number;
    formatted_carry_over_amount: string;
    ending_balance: number;
    formatted_ending_balance: string;
    start_on: string;
    formatted_start_on: string;
    start_on_phrase: string;
    payment_schedule_line: string;
  };
  paywall_coupon?: Pick<
    PaywallCoupon,
    | "id"
    | "code"
    | "duration"
    | "duration_in_months"
    | "amount"
    | "type"
    | "paywall_prices"
    | "all_paywalls"
  >;
  paywall_coupon_id?: number;
  paywall_id: number;
  paywall_price: Pick<
    BasePaywallPrice,
    "id" | "price_type" | "interval_type" | "amount"
  >;
  paywall_price_id: number;
  preview_scenario: PaywallPriceScenario;
  remaining_payments?: {
    total_amount_due: number;
    coupons_applied_quantity: number;
    installments_preview: {
      amount_due: number;
      start_on: string;
      coupon_status: "applied" | "not_applied";
    }[];
  };
  trial_period?: {
    days: number;
    free: boolean;
    paid: boolean;
    ends_on?: string;
    formatted_ends_on?: string;
  };
}

export interface PaywallFailure {
  category: string;
  code: string;
  level: string;
  message: string;
}

export const isPaywallFailure = (error: any): boolean =>
  error &&
  error.failure &&
  error.failure.category &&
  error.failure.code &&
  error.failure.level &&
  error.failure.message;
